<script lang="ts" setup>
import type { ProductDetail } from '~/types/api'

const props = defineProps<{
  product: ProductDetail
  productLineTitle: string | undefined
}>()

const { showPrices } = storeToRefs(useUser())

const minimalFormattedPrice = computed(() =>
  props.product.minimalPrice
    ? formatPrice(props.product.minimalPrice, props.product.currency)
    : undefined
)

const productPrices = computed(() => props.product.productPrices)

const {
  anchor,
  floating: tooltip,
  floatingStyles,
} = useUiFloating({
  offsetSize: 0,
  placement: 'bottom-start',
  strategy: 'fixed',
})

const open = ref(false)

function onMouseEnter() {
  open.value = true
}

function onMouseLeave() {
  open.value = false
}
</script>

<template>
  <div class="flex flex-col justify-between">
    <div>
      <p class="font-pragmatica-ext text-2xl font-bold">
        {{ props.productLineTitle }}
      </p>
      <p class="font-pragmatica-ext font-bold">{{ props.product.title }}</p>
    </div>
    <div>
      <NuxtImg
        v-if="props.product.images[0]?.url"
        sizes="100vw sm:400px"
        class="py-5"
        :src="withHost(props.product.images[0]?.url)"
      />
      <div v-else class="flex min-h-52 w-full grow items-center justify-center">
        <Icon class="text-xl text-black" :name="'fa6-solid:image'" />
      </div>
      <div class="flex flex-wrap items-baseline justify-between gap-2">
        <div
          v-if="showPrices"
          ref="anchor"
          class="font-pragmatica-ext group relative z-30 mt-2 h-max w-max text-nowrap rounded-2xl bg-neutral-100 px-4 py-2 text-sm font-bold"
          :class="[
            {
              'cursor-pointer hover:bg-neutral-200': productPrices.length,
            },
            { 'hover:!bg-neutral-200': productPrices.length },
          ]"
          @mouseenter="onMouseEnter"
          @mouseleave="onMouseLeave"
        >
          {{ $t('base.products.from') }} {{ minimalFormattedPrice }}
          <Icon
            v-if="productPrices.length"
            size="12"
            class="ml-1 group-hover:hidden"
            name="bi:arrow-down"
          />
          <Icon
            v-if="productPrices.length"
            size="12"
            class="ml-1 !hidden group-hover:!inline-block"
            name="bi:arrow-up"
          />
          <div v-if="open" ref="tooltip" :style="floatingStyles" class="py-4">
            <div
              class="z-50 flex flex-col gap-2 rounded-2xl bg-neutral-100 p-4"
            >
              <div
                v-for="productPrice in productPrices"
                :key="productPrice.nid"
                class="flex w-full items-baseline justify-between gap-4"
              >
                <p class="font-sans text-sm font-normal">
                  {{ $t('configurator.category') }}
                  {{ productPrice.surfaceCollectionCategoryTitle }}
                </p>
                <p>
                  {{ formatPrice(productPrice.price, props.product.currency) }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <ProductOrderFormModal
          :product="props.product"
          :product-line-title="props.productLineTitle"
        />
      </div>
    </div>
  </div>
</template>
